import * as React from "react";
import { APIOutput, ContainerRef, DecisionTreeContent, EntityRef, IDecisionTree } from "@amzn/ask-legal-domain";
import { AppContext } from "../setup/context";
import { useLayoutManager } from "./decision-tree-node-hook";
import { BatchedSaveQueue } from "../utils/decisiontree/save-queue";

/**
 * Hook for managing decision tree version refs and content state.
 *
 * Tracks:
 * - currentDraftRef: latest draft version (for API calls)
 * - currentContainerRef: latest container metadata
 * - currentContent: latest tree content (optimistic updates + server responses)
 *
 * Provides:
 * - updateRefsFromOutput: safely update refs from API responses
 * - reloadContainerAndUpdateRefs: reload full container from server
 */
export function useDecisionTreeRefs(props: {
    pageDraftRef: EntityRef;
    containerRef: ContainerRef;
    initialContent: DecisionTreeContent;
    layoutManager: ReturnType<typeof useLayoutManager>;
    saveQueue: React.RefObject<BatchedSaveQueue<any>>;
}) {
    const context = React.useContext(AppContext);

    const currentDraftRef = React.useRef<EntityRef>(props.pageDraftRef);
    const currentContainerRef = React.useRef<ContainerRef>(props.containerRef);
    const currentContent = React.useRef<DecisionTreeContent>(props.initialContent);

    /**
     * Update all refs consistently from API output.
     * Only replaces content when no pending operations remain.
     */
    const updateRefsFromOutput = React.useCallback((output?: IDecisionTree.UpdateDraftOutput) => {
        if (output?.updatedDraftRef) {
            currentDraftRef.current = output.updatedDraftRef;
        }
        if (output?.updatedContainerRef) {
            currentContainerRef.current = output.updatedContainerRef;
        }
        if (output?.content) {
            const hasPendingOps = (props.saveQueue.current?.hasPending() ?? false) ||
                (props.saveQueue.current?.getQueueLength() ?? 0) > 0;
            if (!hasPendingOps) {
                currentContent.current = output.content;
            }
        }
    }, [props.saveQueue]);

    /**
     * Reload container from server and update all refs + UI.
     * Returns true if successful.
     */
    const reloadContainerAndUpdateRefs = React.useCallback(async (): Promise<boolean> => {
        try {
            const rawOutput = await context.getContainerAPI().load(props.containerRef.id);
            const containerOutput = APIOutput.fromRaw(rawOutput.data);

            if (containerOutput.isOk() && containerOutput.data) {
                const containerData = containerOutput.data as any;
                const latestContent = containerData.loadedContent as DecisionTreeContent;
                const latestMetadata = containerData.containerMetadata;

                currentDraftRef.current = latestMetadata.pageDraft || currentDraftRef.current;
                currentContainerRef.current = latestMetadata;

                // Always update both content and layout together to maintain consistency.
                // When pending drag operations exist, merge their positions onto server content
                // so we get the latest structure while preserving unsaved drag positions.
                const hasPendingOps = (props.saveQueue.current?.hasPending() ?? false) ||
                    (props.saveQueue.current?.getQueueLength() ?? 0) > 0;
                if (hasPendingOps) {
                    // Preserve pending drag positions from current content onto server content
                    const mergedContent = {
                        ...latestContent,
                        allNodes: latestContent.allNodes.map(serverNode => {
                            const localNode = currentContent.current.allNodes.find(n => n.id === serverNode.id);
                            return localNode?.position ? { ...serverNode, position: localNode.position } : serverNode;
                        })
                    };
                    currentContent.current = mergedContent;
                    props.layoutManager.update(mergedContent);
                } else {
                    currentContent.current = latestContent;
                    props.layoutManager.update(latestContent);
                }
                return true;
            }
            return false;
        } catch (err) {
            console.error("Failed to reload container:", err);
            return false;
        }
    }, [context, props.containerRef.id, props.layoutManager]);

    return {
        currentDraftRef,
        currentContainerRef,
        currentContent,
        updateRefsFromOutput,
        reloadContainerAndUpdateRefs
    };
}